import { z } from 'zod';
import { FINANCE_REQUEST_PRIORITIES, INVOICE_STATUSES } from '../enums.js';
import { INVOICE_VIEWS } from '../invoiceViews.js';
import { basisPoints, isoDate, minorAmount, objectId, paginationQuery, pan, tdsSectionCode, scopeQuery, } from './common.js';
export const invoiceLineInput = z.object({
    description: z.string().trim().min(1).max(500),
    quantity: z.number().nonnegative().optional(),
    unitPrice: z.number().int().nonnegative().optional(),
    amount: minorAmount,
    hsnSac: z.string().trim().max(20).optional(),
    taxRate: z.number().min(0).max(100).optional(),
});
/** Upper bounds mirror the browser's own OCR limits (20 pages). */
export const RAW_OCR_MAX_PAGES = 20;
export const RAW_OCR_MAX_PAGE_CHARS = 20_000;
/** The laid-out page pads words with spaces to their column, so it runs longer. */
export const RAW_OCR_MAX_LAYOUT_CHARS = 60_000;
/**
 * Text the uploader's browser read from the document, sent alongside the file.
 * It only pre-fills fields a human then reviews, but it is still client input,
 * so its size is bounded.
 */
export const rawOcrUpload = z.object({
    engine: z.string().trim().min(1).max(40),
    pages: z
        .array(z.object({
        page: z.number().int().positive(),
        text: z.string().max(RAW_OCR_MAX_PAGE_CHARS),
        confidence: z.number().min(0).max(100),
        layout: z.string().max(RAW_OCR_MAX_LAYOUT_CHARS).optional(),
        source: z.enum(['TEXT_LAYER', 'OCR']).optional(),
    }))
        .min(1)
        .max(RAW_OCR_MAX_PAGES),
});
const tdsSection = tdsSectionCode;
/** One deduction keyed at accounting; the server works out its amount. */
const tdsLineInput = z.object({
    label: z.string().trim().max(60).optional(),
    section: tdsSection,
    rateBasisPoints: basisPoints.refine((rate) => rate > 0, 'Enter a rate above 0%'),
    baseAmount: minorAmount.refine((amount) => amount > 0, 'Enter the amount TDS applies to'),
});
/** Payload used by the review screen when finance corrects extracted values. */
export const updateInvoiceRequest = z.object({
    vendorId: objectId.optional(),
    vendorName: z.string().trim().max(200).optional(),
    invoiceNumber: z.string().trim().max(80).optional(),
    invoiceDate: isoDate.optional(),
    dueDate: isoDate.optional(),
    // Only the leaves of each axis: the server derives business unit, vertical,
    // region and group from them, so the stored coding cannot contradict itself.
    locationId: objectId.optional().or(z.literal('')),
    departmentId: objectId.optional().or(z.literal('')),
    /** Suggested for accounting, who confirm or change it at verification; empty clears it. */
    suggestedExpenseLedgerId: objectId.optional().or(z.literal('')),
    gstin: z.string().trim().toUpperCase().max(20).optional(),
    /** The supplier's PAN as printed; empty clears it. */
    pan: pan.optional().or(z.literal('')),
    subtotal: minorAmount.optional(),
    taxAmount: minorAmount.optional(),
    totalAmount: minorAmount.optional(),
    /**
     * Confirms the currency the document is billed in. Must equal the company's
     * (an invoice never leaves its company's currency); sending it clears a
     * `CURRENCY_MISMATCH` raised on a misread document.
     */
    currency: z.string().trim().toUpperCase().length(3).optional(),
    lines: z.array(invoiceLineInput).max(500).optional(),
    /**
     * TDS as the reviewer sets it: whether any is deducted, and each deduction's
     * section, rate and base. The rate must be one of the section's rates in
     * the TDS Sections master; accounting confirms or changes it at verification.
     */
    tdsApplicable: z.boolean().optional(),
    tdsLines: z.array(tdsLineInput).max(10).optional(),
    /**
     * Whether the invoice is under GST reverse charge, as the reviewer sets it;
     * accounting confirms or changes it at verification.
     */
    rcmApplicable: z.boolean().optional(),
});
export const createInvoiceRequest = updateInvoiceRequest.extend({
    companyId: objectId,
});
export const resolveFindingRequest = z.object({
    code: z.string().min(1),
    /** `KEEP` continues with the invoice, `DUPLICATE` marks it a duplicate. */
    resolution: z.enum(['KEEP', 'DUPLICATE']),
    note: z.string().trim().min(3).max(500),
});
export const cancelInvoiceRequest = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** Soft-deletes an invoice that has not been submitted for approval. */
export const deleteInvoiceRequest = z.object({
    reason: z.string().trim().min(3, 'Say why it is being deleted').max(500),
});
/**
 * The accounting team's decision on a business-approved invoice.
 *
 * `RELEASE` clears it for payment, `ESCALATE` raises a Treasury request, and
 * `RETURN` sends it back to the originating department. The TDS fields are
 * accepted on every action so a return still records what accounting found.
 *
 * `netPayable` is deliberately absent: the server derives it, so a client can
 * never dictate what the bank pays.
 */
export const verifyInvoiceRequest = z
    .object({
    action: z.enum(['RELEASE', 'ESCALATE', 'RETURN']),
    tdsApplicable: z.boolean(),
    tdsSection: tdsSection.optional().or(z.literal('')),
    tdsRateBasisPoints: basisPoints.optional(),
    tdsBaseAmount: minorAmount.optional(),
    /** Overrides the computed figure. Kept in minor units like every amount. */
    tdsAmount: minorAmount.optional(),
    /**
     * Several deductions on one invoice — e.g. professional fees under 194J and
     * maintenance under 194C. When sent, these replace the single section, rate
     * and base above.
     */
    tdsLines: z.array(tdsLineInput).max(10).optional(),
    /** GST under reverse charge: confirmed or changed here, for reporting only. */
    rcmApplicable: z.boolean().optional(),
    /**
     * How much of the vendor's advance balance to settle this invoice with.
     * Applied on RELEASE and ESCALATE only; the server checks it against the
     * balance and against what is left after TDS.
     */
    advanceAmount: minorAmount.optional(),
    /** Free text from before ledgers existed; still accepted, no longer offered. */
    glCode: z.string().trim().max(40).optional(),
    /**
     * Ledgers from the company's chart of accounts: what was bought, which of the
     * vendor's accounts it is owed on (its default Payable when left out), and the
     * vendor's advance account when an advance is applied.
     */
    expenseLedgerId: objectId.optional(),
    partyLedgerId: objectId.optional(),
    advanceLedgerId: objectId.optional(),
    costCentre: z.string().trim().max(60).optional(),
    notes: z.string().trim().max(1000).optional(),
    priority: z.enum(FINANCE_REQUEST_PRIORITIES).optional(),
    remarks: z.string().trim().max(1000).optional(),
})
    .refine((body) => !body.tdsApplicable || !!body.tdsLines || !!body.tdsSection, {
    message: 'A TDS section is required when TDS applies',
    path: ['tdsSection'],
})
    .refine((body) => !body.tdsApplicable || !!body.tdsLines || (body.tdsRateBasisPoints ?? 0) > 0, {
    message: 'A TDS rate is required when TDS applies',
    path: ['tdsRateBasisPoints'],
})
    .refine((body) => !body.tdsApplicable || !body.tdsLines || body.tdsLines.length > 0, {
    message: 'Add at least one TDS line',
    path: ['tdsLines'],
})
    .refine((body) => body.action !== 'ESCALATE' || !!body.priority, {
    message: 'A Treasury request needs a priority',
    path: ['priority'],
})
    .refine((body) => body.action === 'RELEASE' || (body.remarks?.length ?? 0) >= 3, {
    message: 'Add a remark of at least 3 characters to say why',
    path: ['remarks'],
});
export const invoiceListQuery = paginationQuery.merge(scopeQuery).extend({
    status: z
        .union([
        z.enum(INVOICE_STATUSES),
        z.array(z.enum(INVOICE_STATUSES)),
    ])
        .optional(),
    vendorId: objectId.optional(),
    view: z.enum(INVOICE_VIEWS).optional(),
});
/** Accounts payable views — PRD §16. */
export const payableListQuery = paginationQuery.merge(scopeQuery).extend({
    view: z
        .enum(['ALL', 'DUE_TODAY', 'DUE_THIS_WEEK', 'OVERDUE', 'APPROVED', 'PAYMENT_PENDING', 'PAID'])
        .default('ALL'),
    vendorId: objectId.optional(),
    /** Invoices, loan EMIs and charges, or both. */
    source: z.enum(['ALL', 'INVOICES', 'LOANS']).default('ALL'),
});
export const dueRemindersQuery = z.object({
    companyId: objectId.optional(),
    groupId: objectId.optional(),
    regionId: objectId.optional(),
    verticalId: objectId.optional(),
    businessUnitId: objectId.optional(),
    locationId: objectId.optional(),
    departmentId: objectId.optional(),
    /** The banner needs only the counts; the popup asks for the invoices too. */
    withItems: z.enum(['true', 'false']).optional(),
});
//# sourceMappingURL=invoice.js.map